import { gqlClient } from '@/api/graphql-client';
import { CREATE_UPLOAD_INTENT_MUTATION } from '@/api/mutations/file/useCreateUploadIntent';
import { graphql } from '@/gql';
import { UploadFileInput } from '@/gql/graphql';

const PROCESS_UPLOAD_INTENT = graphql(`
  mutation ProcessUploadIntent($input: ProcessUploadIntentInput!) {
    processUploadIntent(input: $input) {
      id
      fileName
      url
      thumbnailUrl
      mimeType
    }
  }
`);

/**
 * Send a file to storage without it passing through our own server, which
 * cannot receive a request larger than 32MiB: mint a single-use signed URL,
 * `PUT` the bytes to it, then ask the server to attach the finished file.
 *
 * `File.id` is the numeric upload id rather than a public id, but it is declared
 * as a GraphQL `ID`, so it arrives as a string.
 */
export const uploadFileToStorage = async (
  file: File,
  fileName: string,
  linkedEntities: UploadFileInput['linkedEntities']
) => {
  const minted = await gqlClient.request(CREATE_UPLOAD_INTENT_MUTATION, {
    input: {
      files: [
        {
          fileName,
          // A file whose type the browser cannot name would otherwise be signed
          // with an empty content type, which storage rejects.
          mimeType: file.type || 'application/octet-stream',
          linkedEntities,
        },
      ],
      // We attach the file ourselves below, so the storage notification must
      // not race us to it.
      skipAutoProcess: true,
    },
  });

  const { signedUploadUrl, signedUploadUrlHeaders, uploadIntent } =
    minted.createUploadIntent[0];

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUploadUrl, true);
    // Every header issued with the URL is part of its signature, so storage
    // rejects the request if any of them is missing or altered.
    signedUploadUrlHeaders?.forEach(([name, value]) =>
      xhr.setRequestHeader(name, value)
    );

    xhr.onload = () =>
      xhr.status === 200
        ? resolve()
        : reject(new Error(`Upload failed with status ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error'));

    xhr.send(file);
  });

  const { processUploadIntent } = await gqlClient.request(
    PROCESS_UPLOAD_INTENT,
    {
      input: {
        uploadIntent: {
          publicId: uploadIntent.publicId,
          synchronouslyGenerateThumbnail: true,
        },
      },
    }
  );

  return { ...processUploadIntent, id: Number(processUploadIntent.id) };
};
